/**
 * Minimal toast system. No deps — single context, queue of messages,
 * auto-dismiss after 2.5 s. Designed for ``CopyableCell`` ("Copied
 * '<value>'") + job-enqueue confirmations.
 *
 * The provider sits inside ``<AppShell>`` so every v2 page can call
 * ``useToast().push(...)`` without prop drilling. Each toast renders
 * fixed-bottom-right; max 3 visible at a time.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { CheckCircle2, Info, XCircle } from "lucide-react";

import { cn } from "../lib/cn";

type ToastTone = "success" | "error" | "info";

type ToastMsg = {
  id: number;
  tone: ToastTone;
  text: string;
  /** Set ~150ms before removal so the toast can fade out (exit < enter). */
  leaving?: boolean;
};

type ToastContextValue = {
  push: (text: string, tone?: ToastTone) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMsg[]>([]);
  const idRef = useRef(0);

  const push = useCallback((text: string, tone: ToastTone = "info") => {
    const id = ++idRef.current;
    setToasts((cur) => [...cur, { id, tone, text }].slice(-3));
    // Flag the exit a beat before removal so it fades out instead of
    // vanishing, then drop it once the 150ms exit has played.
    setTimeout(() => {
      setToasts((cur) =>
        cur.map((t) => (t.id === id ? { ...t, leaving: true } : t)),
      );
    }, 2500);
    setTimeout(() => {
      setToasts((cur) => cur.filter((t) => t.id !== id));
    }, 2680);
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cn(
              "pointer-events-auto flex max-w-md items-center gap-2 rounded border px-3 py-2 text-sm shadow-lg",
              t.tone === "success"
                ? "border-emerald-700 bg-emerald-900/80 text-emerald-100"
                : t.tone === "error"
                  ? "border-red-700 bg-red-900/80 text-red-100"
                  : "border-slate-700 bg-slate-900/90 text-slate-100",
              // Enter via the one-shot keyframe; on exit cancel it and
              // transition to a subtle faded/slid-down state (softer + shorter
              // than the enter, per the skill).
              t.leaving
                ? "[animation:none] translate-y-1 opacity-0 transition-[opacity,transform] duration-150 ease-in"
                : "animate-enter",
            )}
            role="status"
          >
            {t.tone === "success" ? (
              <CheckCircle2 className="h-4 w-4 shrink-0" />
            ) : t.tone === "error" ? (
              <XCircle className="h-4 w-4 shrink-0" />
            ) : (
              <Info className="h-4 w-4 shrink-0" />
            )}
            <span className="break-words">{t.text}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used inside a <ToastProvider>");
  }
  return ctx;
}
